import React, { useState } from 'react';
import authService from '../../api-authorization/AuthorizeService';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';

// Admin tool to find and repair chess games that were imported with a blank
// White or Black player name. Counting is a read-only query; fixing walks each
// affected game and looks up the name from the ChessPlayer table using the
// stored FIDE ID or USCF ID (USCFName is used when FIDEName is empty).
export default function RepairBlankNameGames() {
    const [counting, setCounting] = useState(false);
    const [fixing, setFixing] = useState(false);
    const [count, setCount] = useState(null);
    const [fixResult, setFixResult] = useState(null);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");

    const batchSize = 500;

    const getHeaders = async () => {
        const t = await authService.getAccessToken();
        return { headers: { 'Authorization': `Bearer ${t}` } };
    };

    const handleCount = async () => {
        setError("");
        setStatus("");
        setFixResult(null);
        setCounting(true);
        try {
            const headers = await getHeaders();
            const response = await axios.get('/api/ChessGames/CountBlankNameGames', headers);
            setCount(response.data);
        } catch (err) {
            console.log(JSON.stringify(err));
            setError("Failed to count games with blank names. Please check the log for details.");
        } finally {
            setCounting(false);
        }
    };

    const handleFix = async () => {
        setError("");
        setStatus("");
        setFixing(true);
        try {
            const headers = await getHeaders();
            let totalProcessed = 0;
            let totalFixed = 0;
            let totalUnresolved = 0;
            let remaining = 0;

            // Repair in batches until no affected games remain (or a batch makes no progress).
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const response = await axios.post(
                    `/api/ChessGames/FixBlankNameGames?batchSize=${batchSize}`,
                    null,
                    headers
                );
                const data = response.data;
                totalProcessed += data.processed;
                totalFixed += data.fixedCount;
                totalUnresolved += data.unresolvedCount;
                remaining = data.remaining;

                setStatus(`Processed ${totalProcessed} games, fixed ${totalFixed}, ${remaining} remaining...`);

                // Stop when nothing left, or when this batch could not make progress
                // (all remaining games are unresolvable via FIDE/USCF ID lookup).
                if (data.processed === 0 || data.fixedCount === 0) {
                    break;
                }
            }

            setFixResult({ totalProcessed, totalFixed, totalUnresolved, remaining });
            setCount(prev => (prev ? { ...prev, totalGames: remaining } : prev));
        } catch (err) {
            console.log(JSON.stringify(err));
            setError("Failed to fix games with blank names. Please check the log for details.");
        } finally {
            setFixing(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Repair Games With Blank Player Names
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                Some games were imported with a blank White or Black name. Use&nbsp;
                <strong>Count Blank Name Games</strong> to see how many exist, then&nbsp;
                <strong>Fix Blank Name Games</strong> to look up each player's name by FIDE ID
                or USCF ID and update the game.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <LoadingButton
                    variant="outlined"
                    loading={counting}
                    disabled={fixing}
                    onClick={handleCount}
                >
                    Count Blank Name Games
                </LoadingButton>
                <LoadingButton
                    variant="contained"
                    color="warning"
                    loading={fixing}
                    disabled={counting}
                    onClick={handleFix}
                >
                    Fix Blank Name Games
                </LoadingButton>
            </Stack>

            {count && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Games with a blank name: <strong>{count.totalGames}</strong>
                    {' '}(blank White: {count.blankWhite}, blank Black: {count.blankBlack})
                </Alert>
            )}

            {status && (
                <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                    {status}
                </Typography>
            )}

            {fixResult && (
                <Alert severity={fixResult.remaining > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
                    Fixed <strong>{fixResult.totalFixed}</strong> games.
                    {' '}Processed {fixResult.totalProcessed}, {fixResult.remaining} still have a blank name
                    {fixResult.remaining > 0
                        ? " (could not resolve a name from the FIDE/USCF ID)."
                        : "."}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
}
